import React, { useRef, useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import InputWithChecks from "../../shared/InputWithChecks";
import Container from "../../shared/Container";
import PrimaryButton from "../../shared/PrimaryButton";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import { validateRequired, validateForm, validateMinLength } from "../../../util/shared/validation";
import { addCollection, updateCollection } from "../../../util/admin/httpAdmin";
import { queryClient } from "../../../util/admin/httpAdmin";
import SuccessMessage from "../../shared/SuccessMessage";
import IconSelector from "./IconSelector";

const CollectionFormAdmin = ({ onSuccessClose, editMode = false, previousData = {} }) => {
    const nameRef = useRef(null);
    const [selectedIcon, setSelectedIcon] = useState(editMode ? previousData.icon : null);
    const [successMessage, setSuccessMessage] = useState("");
    const closeTimeoutRef = useRef(null);
    const messageTimeoutRef = useRef(null);

    useEffect(() => {
        // Cleanup function
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
            if (messageTimeoutRef.current) {
                clearTimeout(messageTimeoutRef.current);
            }
        };
    }, []);

    const mutationFn = editMode ? updateCollection : addCollection;

    const { mutate, isPending, isError, error } = useMutation({
        mutationFn,
        onSuccess: () => {
            setSuccessMessage(
                editMode
                    ? "Collectie succesvol bijgewerkt!"
                    : "Collectie succesvol toegevoegd!"
            );
            queryClient.invalidateQueries(["adminCollections"]);
            
            closeTimeoutRef.current = setTimeout(() => {
                if (onSuccessClose) {
                    onSuccessClose();
                }
                messageTimeoutRef.current = setTimeout(() => {
                    setSuccessMessage("");
                }, 500);
            }, 500);
        },
    });

    const handleFormSubmit = (e) => {
        e.preventDefault();

        const nameValid = nameRef.current?.validateInput();

        if (!selectedIcon) {
            alert("Selecteer een icoon voor de collectie");
            return;
        }

        if (nameValid) {
            const formData = {
                name: nameRef.current.getValue(),
                icon: selectedIcon
            };

            if (editMode && previousData?._id) {
                formData._id = previousData._id;
            }

            mutate(formData);
        }
    };

    return (
        <section>
            <Container>
                {successMessage ? (
                    <SuccessMessage title="Succes!" description={successMessage} />
                ) : (
                    <BoxBorder extraClasses="mxw-800 m-a g-20">
                        <h2>{editMode ? "Collectie Bewerken" : "Nieuwe Collectie"}</h2>
                        <form className="g-20">
                            <InputWithChecks
                                ref={nameRef}
                                label="Collectienaam"
                                inputType="text"
                                validate={(value) =>
                                    validateForm(value, [
                                        validateRequired,
                                        (v) => validateMinLength(v, 2),
                                    ])
                                }
                                placeholder="Voer de collectienaam in"
                                value={editMode ? previousData.name : ""}
                            />
                            
                            <IconSelector 
                                selectedIcon={selectedIcon} 
                                onSelectIcon={setSelectedIcon}
                            />

                            {isError && (
                                <div className="c-r">
                                    {error?.info?.message || "Er is iets fout gegaan tijdens het verwerken van de collectie."}
                                </div>
                            )}

                            <div>
                                <PrimaryButton onClick={handleFormSubmit} disabled={isPending}>
                                    {editMode ? "Collectie Bijwerken" : "Collectie Toevoegen"}
                                    {isPending && <LoadingIndicatorButton />}
                                </PrimaryButton>
                            </div>
                        </form>
                    </BoxBorder>
                )}
            </Container>
        </section>
    );
};

export default CollectionFormAdmin; 